import { AdComposedPlayer, Player, TrackInfo, TrackType } from 'fl-player-interface';
import { HasAdInfo, PlaybackStatistics } from 'qp-store';
import { ResourceVm } from 'qp-ui-interface';
import { getPercentage } from 'src/app/shared/utils/EventUtlis';
import { LocalStorageKeys } from '../../../shared/LocalStorageKeys';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';
import { ScreenService } from '../../../shared/services/screen/screen.service';
import { getSelectedWatchLangName } from '../../../shared/utils/LanguageUtils';
import { SubtitleInfo } from '../models/newrelic.model';

const getActiveLanguage = (trackType: TrackType, player: Player): any => {
  const activeAudioTrack = player?.getActiveTracks()?.find((track: TrackInfo) => {
    return track.type === trackType;
  });

  return activeAudioTrack?.language || 'OFF';
};

export const getSubtitleInfo = (player: Player, localStorage: LocalStorageService): SubtitleInfo => {
  const ccEnabled = player
    ? !!(player?.getActiveTracks()?.find((element) => element?.type === 'text') !== undefined)
    : localStorage.getItem(LocalStorageKeys.SUBTITLES_LANGUAGE) !== 'off';
  return {
    audioLanguage: getSelectedWatchLangName(localStorage),
    subtitleLanguage: getActiveLanguage(TrackType.TEXT, player),
    ccEnabled,
  };
};

export const getNRPlaybackStatistics = (
  player: Player,
  resource: ResourceVm,
  playbackStartedTime: number,
  screenService: ScreenService,
  localStorage: LocalStorageService
): PlaybackStatistics => {
  const playbackStatistics = player?.playbackStatistics;
  const runningTime = resource?.runningTime;
  const durationHour = runningTime?.includes('h') ? parseInt(runningTime, 10) * 3600 : '';
  const durationMin = durationHour !== '' ? parseInt(runningTime?.split(' ')[1], 10) * 60 : parseInt(runningTime, 10) * 60;
  const contentDuration = durationHour !== '' ? durationHour + durationMin : durationMin;
  const playerPosition = isNaN(player?.currentTime) || player?.currentTime === 0 ? -1 : player?.currentTime;
  const playbackStartedTimeSec =
    playbackStartedTime === null ? playbackStartedTime : playbackStartedTime <= 0 || playbackStartedTime > 50 ? 0 : playbackStartedTime;

  return {
    contentDurationSec: player?.duration ? player?.duration : contentDuration,
    videoResolution: `${playbackStatistics?.width}:${playbackStatistics?.height}`,
    currentBandwidthKb:
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      playbackStatistics?.variantBitrate || window.navigator?.connection?.downlink,
    highestVariantBandwidthKb: player?.availableBitrates?.[player?.availableBitrates?.length - 1] || -1,
    lowestVariantBandwidthKb: player?.availableBitrates?.[0] || -1,
    lastPlayedVariant: playbackStatistics?.variantBitrate,
    lastAttemptedVariant: playbackStatistics?.variantBitrate,
    observedBandwidth: playbackStatistics?.requiredBandwidth || 0,
    videoBitrate: playbackStatistics?.videoBitrate || 0,
    currentPosition: playerPosition,
    playbackDurationSec: player ? Math.floor(playbackStatistics?.playTime) : 0,
    programPlaybackDurationSec: player ? Math.floor(playbackStatistics?.programWatchedDuration || playbackStatistics?.playTime) : 0,
    playbackStartedTimeSec,
    timeToFirstByte: 0,
    bufferCount: 0,
    bufferTime: playbackStatistics?.bufferingTime,
    seekEndTime: 0,
    seekStartTime: 0,
    indicatedBitrateKbps: playbackStatistics?.estimatedBandwidth,
    source: screenService.previousScreen,
    streamPercentage: getPercentage(contentDuration * 1000, playerPosition),
    ...getSubtitleInfo(player, localStorage),
  };
};

/**
 * Get CuePoints for ad to determine hasAds & hasPreroll
 *
 * @param adplayer
 */

export const getHasAdInfo = (player: Player): HasAdInfo => {
  try {
    const cuepoints = (player as AdComposedPlayer).getCuePoints();
    return {
      hasAds: cuepoints?.includes(0) || false,
      hasPreroll: cuepoints?.includes(0) || false,
    };
  } catch (error) {
    return {
      hasAds: false,
      hasPreroll: false,
    };
  }
};
